import { InputFormat } from '../input-format.interfaces';
import { FormatInstanceCollection, FormatInstanceCollectionManager } from './format-instance-collection';
import { FormatNavigator } from './format-navigator';
import { FormatRenderer } from './format-renderer';
import { InputRuleProcessor } from './input-rule-processor';
import { KeyTypeChecker } from './key-type-checker';
import { MovementKeyboardEvent } from './parser.interfaces';

/**
 * Processes any key press and determines where it needs to be handled.
 */
export class KeyProcessor {
  private readonly instanceCollection: FormatInstanceCollection;
  private readonly formatNavigator: FormatNavigator;
  private readonly ruleProcessor: InputRuleProcessor;
  private readonly keyTypeChecker: KeyTypeChecker;
  private readonly formatRenderer: FormatRenderer;

  constructor(format: InputFormat, instanceId: string) {
    this.instanceCollection = FormatInstanceCollectionManager.getInstance();
    this.formatNavigator = this.instanceCollection.getNavigatorInstance(instanceId, format);
    this.ruleProcessor = new InputRuleProcessor(format, instanceId);
    this.keyTypeChecker = new KeyTypeChecker();
    this.formatRenderer = new FormatRenderer(format, instanceId);
  }

  public setInputElement(element: HTMLElement): void {
    this.ruleProcessor.setInputElement(element);
    this.formatRenderer.setInputElement(element);
  }

  /**
   * Handles movement key presses.
   * @param {MovementKeyboardEvent} event - A specific movement key event.
   */
  public processMovementKey(event: MovementKeyboardEvent) {
    const { key, metaKey, ctrlKey, shiftKey } = event;
    switch (key) {
      case 'ArrowLeft':
        if (!shiftKey) {
          if (!metaKey) {
            // move the cursor to the left
            this.formatNavigator.moveCursorLeft();
          } else {
            // on Mac home is a combination of the command key and the left arrow key
            this.formatNavigator.moveHome();
          }
        } else {
          // the shift key is used to highlight text
          this.formatNavigator.moveHighlightLeft();
        }

        break;
      case 'ArrowRight':
        if (!shiftKey) {
          if (!metaKey) {
            // move the cursor to the right
            this.formatNavigator.moveCursorRight();
          } else {
            // on Mac end is a combination of the command key and the right arrow key
            this.formatNavigator.moveEnd();
          }
        } else {
          // the shift key is used to highlight text
          this.formatNavigator.moveHighlightRight();
        }

        break;
      case 'Home':
        this.formatNavigator.moveHome();
        break;
      case 'End':
        this.formatNavigator.moveEnd();
        break;
      case 'a':
        if (metaKey || ctrlKey) {
          // mac command or windows control key + a is used to select all text
          this.formatNavigator.highlightAll();
        }
        break;
    }
  }

  /**
   * Processes the keypress event and returns true if the key pressed results in a format change. I'm not a fan of
   * returning true or false to indicate whether the format was changed, so I'm going to fix this later.
   * @param {KeyboardEvent} event - The key event to process.
   * @returns {boolean} True if the key pressed results in a format change.
   */
  public processKeyPress(event: KeyboardEvent) {
    if (this.keyTypeChecker.isIgnoreKey(event)) {
      return false;
    }

    if (this.keyTypeChecker.isMovementKey(event)) {
      this.processMovementKey(event);
      return false;
    }

    this.ruleProcessor.processKeyPress(event);
    return true;
  }
}
